import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '@/hooks/usePageTitle'
import { ProgramFaq, type FaqItem } from '@/features/program/ProgramFaq'
import { CAREER_TLDP, useProgramVacancy } from '@/features/program/queries'

/** Only the first FAQ group differs between the two program pages. */
const TLDP_FAQS: FaqItem[] = [
  {
    question: 'What is the minimum education requirement for candidates?',
    answer: 'This program is open to graduates of D4, S1, S2, and equivalent.',
  },
  { question: 'Is it open to all majors?', answer: 'Yes, this program is open to all majors.' },
  {
    question: 'How long does the On-the-Job Training (OJT) last?',
    answer: 'The On-the-Job Training (OJT) process lasts for 1 year.',
  },
  {
    question: 'Will the degree certificate be withheld during On-the-Job Training?',
    answer: 'No, there is no withholding of the degree certificate.',
  },
  { question: 'Where will the placement be?', answer: 'Placement will be in all areas of TBIG.' },
]

const LOOKING_FOR = [
  { text: "Bachelor's or Master's Degree in any major", icon: 'img_slide_3_6.png' },
  { text: 'Fresh graduate or max 2 years of experience', icon: 'img_slide_3_4.png' },
  { text: 'Minimum GPA of 3.00', icon: 'img_slide_3_7.png' },
  { text: 'Strong leadership skills', icon: 'img_slide_3_2.png' },
  { text: 'Proactive and goal-driven', icon: 'img_slide_3_5.png' },
  { text: 'Willing to be placed in any function', icon: 'img_slide_3_3.png' },
]

const GRADUATES = [
  {
    image: 'img_slide_6_1.jpg',
    name: 'Tri Cahyani',
    role: 'NOC Shift Section Head',
    quote:
      'As a TLDP, you’re not just observing the future of leadership. It is the art of transforming ambition into expertise, as you navigate challenges and embrace opportunities to lead with purpose.',
  },
  {
    image: 'img_slide_6_4.jpg',
    name: 'Gaguk Supriyanto',
    role: 'Area Operation Division Head',
    quote:
      'TLDP will challenge you beyond your limits, but the rewards are immense. Embrace the learning, build strong leadership skills, and never stop growing.',
  },
  {
    image: 'img_slide_6_3.jpg',
    name: 'Muhammad Ali Yusuf',
    role: 'Project Deployment & Controlling Department Haed',
    quote:
      "Being a TLDP is more than developing leadership skills. It's a journey of growth and learning, where every challenge becomes a stepping stone, every opportunity broadens your perspective, and every experience shapes you into a better version of yourself—professionally and personally.",
  },
]

declare global {
  interface Window {
    Swiper?: new (selector: string, options: Record<string, unknown>) => { destroy: () => void }
  }
}

/**
 * Port of Views/Program/TLDP.cshtml.
 *
 * BUG FIXED, deliberately: the Apply button linked to
 * `/Vacancy/Form/@Model.trxVacancyID` — a RAW integer id on the route that
 * expects an AES-encoded one. DecodeId threw on every click, HandleDecodeError
 * caught it and bounced the candidate to the vacancy listing, so "Apply Now"
 * never opened the TLDP form. It now uses the encoded id the BFF returns, which
 * is what the `/Vacancy/Apply/{id:int}` bounce route existed to produce.
 * TBIGIN.cshtml had the identical defect; see ProgramTBIGIn.tsx.
 */
export function ProgramTLDP(): JSX.Element {
  usePageTitle('TLDP Page')
  const { data } = useProgramVacancy(CAREER_TLDP)
  const swiperRef = useRef<{ destroy: () => void } | null>(null)

  // The page's inline <script>: a coverflow Swiper over the graduate cards.
  // Options are copied verbatim. It is constructed after mount so the slides
  // exist, and torn down on unmount — the Razor page never had to, because
  // leaving the page threw the whole document away.
  useEffect(() => {
    if (!window.Swiper) return
    swiperRef.current = new window.Swiper('.mySwiper', {
      effect: 'coverflow',
      grabCursor: true,
      centeredSlides: true,
      slidesPerView: 'auto',
      coverflowEffect: { rotate: 0, stretch: 0, depth: 300, modifier: 1, slideShadows: false },
      pagination: { el: '.swiper-pagination' },
    })
    return () => {
      swiperRef.current?.destroy()
      swiperRef.current = null
    }
  }, [])

  return (
    <>
      <section className="bg-tldp">
        <div className="tldp-content ">
          <div className="row">
            <div className="col-5 tldp-header-info">
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font"
                data-wow-delay="0.2s"
              >
                <span>TBIG</span>
              </h1>
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font"
                data-wow-delay="0.2s"
              >
                Leader
              </h1>
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font"
                data-wow-delay="0.2s"
              >
                Development
              </h1>
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font"
                data-wow-delay="0.2s"
              >
                Program
              </h1>
              {data?.encodedTrxVacancyID && (
                <Link
                  to={`/Vacancy/Form/${data.encodedTrxVacancyID}`}
                  className="btn btn-info btn-tldp-register mt-3"
                >
                  Apply Now
                </Link>
              )}
            </div>
            <div className="col-7">
              <img className="img1" src="/lib/images/landing/TLDP/img_slide_1_2.png" alt="" />
              <img className="img2" src="/lib/images/landing/TLDP/img_slide_1_1.png" alt="" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white pb-0">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-12 col-xl-6 pt-3">
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font text-TBG-center"
                data-wow-delay="0.2s"
              >
                <span>TBIG</span> Leader
              </h1>
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font text-TBG-center"
                data-wow-delay="0.2s"
              >
                Development
              </h1>
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font text-TBG-center"
                data-wow-delay="0.2s"
              >
                Program
              </h1>
              <p
                className="p-text pt-5 wow animate__fadeInUp"
                data-wow-delay="0.6s"
                style={{
                  visibility: 'visible',
                  animationDelay: '0.6s',
                  animationName: 'fadeInUp',
                }}
              >
                Known as the management trainee program, offers a series of in-class and on-the-job
                trainings to develop high-performing talents with a strong drive to become resilient
                and adaptive future leaders of TBIG.
              </p>
            </div>
            <div className="col-md-12 col-xl-6 pt-3">
              <div className="card-tldp">
                <img
                  className="image-bottom"
                  src="/lib/images/landing/TLDP/img_slide_2_2.png"
                  alt=""
                />
                <img className="image-top" src="/lib/images/landing/TLDP/img_slide_2_1.png" alt="" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white pt-0">
        <div className="container pt-5">
          <div className="row justify-content-center">
            <div className="col-12">
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font text-TBG-center"
                data-wow-delay="0.2s"
              >
                What are <span>we</span>
              </h1>
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font text-TBG-center"
                data-wow-delay="0.2s"
              >
                <span>looking</span> for?
              </h1>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-md-12 col-xl-5">
              <div className="crop-container">
                <img src="/lib/images/landing/TLDP/img_slide_3_1.png" alt="" />
              </div>
            </div>
            <div className="col-md-12 col-xl-7 pt-5">
              <div className="row justify-content-center text-center gy-sm-4 gy-3">
                {LOOKING_FOR.map((item) => (
                  <div key={item.icon} className="col-xl-6 col-12">
                    <div
                      className="card-info wow animate__fadeInUp"
                      data-wow-delay="0.6s"
                      style={{
                        visibility: 'visible',
                        animationDelay: '0.6s',
                        animationName: 'fadeInUp',
                      }}
                    >
                      <div className="text">{item.text}</div>
                      <img src={`/lib/images/landing/TLDP/${item.icon}`} alt="icon" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white pt-0">
        <div className="container bg-tldp-2 pt-5">
          <div className="row justify-content-center">
            <div className="col-md-12 col-xl-6">
              <h1
                className="wow animate__fadeInUp section-title text-TBG large-font text-TBG-center"
                data-wow-delay="0.2s"
                style={{ marginTop: 120 }}
              >
                What is the <span>application process</span> be like?
              </h1>
            </div>
            <div className="col-md-12 col-xl-6">
              <div className="card-tldp-registration">
                <div className="card-tldp-registration-inner">
                  {/* Bootstrap's own carousel: data-bs-ride wires it up from
                      bootstrap.min.js, which is loaded in index.html. */}
                  <div id="carouselExampleIndicators" className="carousel slide" data-bs-ride="carousel">
                    <ol className="carousel-indicators">
                      {[0, 1, 2, 3, 4, 5, 6].map((index) => (
                        <li
                          key={index}
                          data-bs-target="#carouselExampleIndicators"
                          data-bs-slide-to={index}
                          className={index === 0 ? 'active' : undefined}
                          style={{ backgroundColor: '#000000' }}
                        />
                      ))}
                    </ol>
                    <div className="carousel-inner">
                      {[1, 2, 3, 4, 5, 6, 7].map((n, index) => (
                        <div key={n} className={`carousel-item${index === 0 ? ' active' : ''}`}>
                          <img
                            className="img-fluid d-block w-100 img-registration"
                            src={`/lib/images/landing/TLDP/img_slide_4_${n}.png`}
                            alt=""
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    className="img-btn"
                    type="button"
                    data-bs-target="#carouselExampleIndicators"
                    data-bs-slide="next"
                  >
                    <img src="/lib/images/landing/TLDP/img_slide_4_8.png" alt="" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white bg-tldp-3 pt-0">
        <div className="container pt-5">
          <div className="row justify-content-center">
            <div className="col-md-12 col-xl-6">
              <h1
                className="wow animate__fadeInUp section-title text-TBG-white large-font text-TBG-center"
                data-wow-delay="0.2s"
                style={{ marginTop: '30%' }}
              >
                TLDP
              </h1>
              <h1
                className="wow animate__fadeInUp section-title text-TBG-stroke large-font text-TBG-center"
                data-wow-delay="0.2s"
              >
                Activities
              </h1>
            </div>
            <div className="col-md-12 col-xl-6">
              <div className="random-cards">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <div key={n} className="card-section-5">
                    <img
                      src={`/lib/images/landing/TLDP/img_slide_5_${n}.png`}
                      alt="Image 1"
                      className="img-main"
                    />
                    <img
                      src="/lib/images/landing/TLDP/img_slide_5_7.png"
                      alt="Image 1"
                      className="corner-image"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white bg-tldp-4 pt-0">
        <div className="container pt-5">
          <div className="row justify-content-center">
            <div className="col-md-12 col-xl-6">
              <h1
                className="wow animate__fadeInUp section-title text-TBG-white text-TBG-center vertical-line"
                data-wow-delay="0.2s"
                style={{ marginTop: '30%' }}
              >
                HEAR FROM OUR TLDP GRADUATES
              </h1>
            </div>
            <div className="col-md-12 col-xl-6">
              <div className="swiper mySwiper">
                <div className="swiper-wrapper">
                  {GRADUATES.map((person) => (
                    <div key={person.name} className="card-swiper swiper-slide">
                      <div className="card__image">
                        <img src={`/lib/images/landing/TLDP/${person.image}`} alt="card image" />
                      </div>
                      <div className="card__content">
                        <span className="card__title">{person.name}</span>
                        <span className="card__name">{person.role}</span>
                        <p className="card__text">{person.quote}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProgramFaq idPrefix="tldp" programFaqs={TLDP_FAQS} />
    </>
  )
}
